/**
 * QRScannerModal — getUserMedia-backed QR reader using html5-qrcode.
 *
 * Renders a live camera viewfinder that emits `onResult(rawDecodedText)`
 * as soon as any QR / Code128 barcode is decoded. Falls back to a manual
 * text input if the camera is unavailable (kiosk PC, no permission, etc.)
 * so the flow never dead-ends.
 */
import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { X, Camera, Keyboard, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function QRScannerModal({ onResult, onClose, title = 'Scan Mark QR / Barcode' }) {
  const scannerId = 'qr-scanner-canvas';
  const scannerRef = useRef(null);
  const [starting, setStarting] = useState(true);
  const [camError, setCamError] = useState(null);
  const [manual, setManual] = useState('');
  const [showManual, setShowManual] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let instance = null;
    (async () => {
      try {
        setStarting(true);
        instance = new Html5Qrcode(scannerId, { verbose: false });
        scannerRef.current = instance;
        // Try back-facing (env) camera first — falls back to any camera
        const cameras = await Html5Qrcode.getCameras().catch(() => []);
        const preferred = cameras.find((c) => /back|rear|environment/i.test(c.label)) || cameras[0];
        if (!preferred) throw new Error('No camera detected');
        await instance.start(
          preferred.id,
          { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0 },
          (decoded) => {
            if (cancelled) return;
            cancelled = true;
            try { instance.stop().then(() => instance.clear()); } catch {}
            onResult(decoded);
          },
          () => { /* per-frame decode errors ignored */ },
        );
        if (!cancelled) setStarting(false);
      } catch (e) {
        if (!cancelled) {
          setCamError(e?.message || 'Camera unavailable');
          setShowManual(true);
          setStarting(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      if (scannerRef.current) {
        try { scannerRef.current.stop().then(() => scannerRef.current.clear()); }
        catch { /* already stopped */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitManual = () => {
    const v = manual.trim();
    if (v) onResult(v);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4"
         style={{ background: 'rgba(0,0,0,0.75)' }}
         data-testid="qr-scanner-modal">
      <div className="w-full max-w-md rounded-xl shadow-2xl overflow-hidden"
           style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
        <div className="flex items-center justify-between p-3 border-b" style={{ borderColor: 'var(--border-color)' }}>
          <div className="flex items-center gap-2">
            <Camera className="w-4 h-4" style={{ color: '#f59e0b' }} />
            <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{title}</h3>
          </div>
          <button onClick={onClose} data-testid="qr-scanner-close">
            <X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
          </button>
        </div>
        <div className="p-3 space-y-2">
          {!showManual && (
            <>
              <div id={scannerId} className="w-full rounded overflow-hidden bg-black"
                   style={{ minHeight: 300 }} data-testid="qr-scanner-canvas" />
              {starting && (
                <div className="flex items-center justify-center gap-2 text-[11px]"
                     style={{ color: 'var(--text-muted)' }}>
                  <Loader2 className="w-3 h-3 animate-spin" /> Requesting camera…
                </div>
              )}
              {camError && (
                <p className="text-[11px]" style={{ color: '#ef4444' }}>{camError}</p>
              )}
              <button onClick={() => setShowManual(true)}
                      className="text-[10px] inline-flex items-center gap-1 underline"
                      style={{ color: 'var(--text-muted)' }}>
                <Keyboard className="w-3 h-3" /> Enter code manually instead
              </button>
            </>
          )}
          {showManual && (
            <div className="space-y-2">
              <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                Paste the mark number or QR payload JSON:
              </p>
              <textarea
                value={manual}
                onChange={(e) => setManual(e.target.value)}
                rows={3}
                placeholder='MK-0001 or {"v":1,"p":"proj_001","m":"/PNOD-012","sn":"..."}'
                className="w-full px-2 py-1.5 rounded border text-[11px] font-mono"
                style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                data-testid="qr-scanner-manual-input"
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
                <Button size="sm"
                        onClick={submitManual}
                        disabled={!manual.trim()}
                        style={{ background: '#f59e0b', color: '#fff' }}
                        data-testid="qr-scanner-manual-submit">
                  Submit
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
